import { MirrorData, SetMirrorData } from '@type/job-add';
import { RecordInterface, GlobalValuesTypes } from '@components/common/Forms';
import { useTranslation } from 'next-i18next';
import { useJobAddresses } from '@components/templates/job-add/edit/hooks/useJobAddresses';
import { JobAddress } from '@components/templates/job-add/edit/form/JobAddress';
import { JobMaxLocations } from '@components/templates/job-add/edit/form/partials/JobMaxLocations';
import { JobWorkPlacement } from '@components/templates/job-add/edit/form/JobWorkPlacement';

/** Props */
interface JobAddressesGroupProps {
  record: RecordInterface,
  getFieldsValue: (name: string) => GlobalValuesTypes,
  googleApiKey: string,
  initialAddresses?: string[],
  initialWorkPlacement?: ApiSchema<'JobModel3'>['workPlacement'],
  reqMirrorData?: Pick<MirrorData, | 'currentActivationsGroup'>,
  setMirrorData?: SetMirrorData,
}

/**
 * @description Job Addresses Group component. To be used in a form.
 * - `record` - record function from useBjForm
 * - `getFieldsValue` - get fields value function from useBjForm
 * - `googleApiKey` - Google API key
 * - `initialAddresses` - initial addresses; default is empty array
 * - `initialWorkPlacement` - initial work placement; default is 'remote'
 * - `reqMirrorData`: The required form mirror data.
 * - `setMirrorData` - function to update the mirror form state
 */
export const JobAddressesGroup = (props: JobAddressesGroupProps) => {
  // Destructure props
  const {
    record,
    getFieldsValue,
    googleApiKey,
    initialAddresses,
    initialWorkPlacement,
    reqMirrorData,
    setMirrorData,
  } = props;

  // Translation
  const { t } = useTranslation();

  const {
    addresses,
    isRemote,
    maxLocations,
    setIsRemote,
    addAddress,
    changeAddress,
    deleteAddress,
    saveAddresses,
    addressesRecordOptions,
  } = useJobAddresses({
    getFieldsValue,
    initialAddresses,
    initialWorkPlacement,
    reqMirrorData,
    setMirrorData,
  });

  // Render
  // **************************
  return (
    <>
      {/* Job Addresses Group */}
      <div className="px-5 text-sm font-bold text-ink-medium after:ml-1 after:text-error after:content-['*']">
        {t('job.add.label.job_location')}
        <span className="ml-1.5 font-normal text-ink-light">
          {t('job.add.label.job_location_hint')}
        </span>
      </div>

      {/* Job work placement */}
      <JobWorkPlacement
        record={record}
        setIsRemote={setIsRemote}
        saveAddresses={saveAddresses}
      />

      {/* Job addresses */}
      {!isRemote && (
        <div className="mt-2 grid grid-cols-1 gap-y-2">
          {addressesRecordOptions.map((address, index) => (
            <JobAddress
              key={address.defaultValue as string || address.id}
              record={record}
              recordOptions={address}
              googleApiKey={googleApiKey}
              showAddMore={index === addressesRecordOptions.length - 1}
              showDelete={addressesRecordOptions.length > 1}
              currentAddress={addresses[index]}
              addAddress={addAddress}
              deleteAddress={() => deleteAddress(index)}
              changeAddress={(value) => changeAddress(index, value)}
              saveAddresses={saveAddresses}
              currentIndex={index}
            />
          ))}
        </div>
      )}

      {/* Max locations */}
      <JobMaxLocations maxLocations={maxLocations} />
    </>
  );
};
